import { ElementType, ReactNode } from 'react';
import { useLightestOutline } from './use-lightest-outline';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Cancel from '@mui/icons-material/Cancel';
import FeedOutlined from '@mui/icons-material/FeedOutlined';

interface SelectedFileRowProps {
  name: string;
  onRemove: () => void;
  icon?: ElementType;
  /** Shown on the right of the row, e.g. a Continue button. */
  action?: ReactNode;
  width?: number | string;
}

export default function SelectedFileRow({
  name,
  onRemove,
  icon: Icon = FeedOutlined,
  action,
  width = '100%',
}: SelectedFileRowProps) {
  const theme = useTheme();
  const lightestOutline = useLightestOutline();

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={4}
      sx={{
        width,
        minHeight: 60,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: lightestOutline,
        backgroundColor: theme.palette.semantic.surface.bg1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Icon sx={{ color: theme.palette.semantic.icon.primary }} />
        <Tooltip title={name} arrow>
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: theme.palette.semantic.text.title2,
              // Full-width rows push the remove icon to the right edge.
              flex: action ? undefined : 1,
            }}
          >
            {name}
          </Typography>
        </Tooltip>
        <IconButton
          size="small"
          aria-label="Remove file"
          onClick={onRemove}
          sx={{ p: 0 }}
        >
          <Cancel sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
      {action}
    </Stack>
  );
}
